import { supabase } from '@/lib/supabase';

export const LOGOS_BUCKET = 'project-logos';
export const CONTRACTS_BUCKET = 'project-contracts';

/** Convierte un nombre de archivo en una clave válida (minúsculas, sin espacios ni acentos). */
export function sanitizeFileName(name: string): string {
  const dot = name.lastIndexOf('.');
  const base = dot > 0 ? name.slice(0, dot) : name;
  const ext = dot > 0 ? name.slice(dot).toLowerCase() : '';
  const clean =
    base
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60) || 'archivo';
  return `${clean}${ext}`;
}

function buildPath(folder: string, file: File): string {
  const stamp = `${Date.now()}-${Math.floor(Math.random() * 10000)}`;
  return `${folder}/${stamp}-${sanitizeFileName(file.name)}`;
}

/** Sube un archivo a un bucket público y devuelve la URL pública. */
export async function uploadPublicFile(
  bucket: string,
  file: File,
  folder = 'items',
): Promise<string> {
  const path = buildPath(folder, file);
  const { error } = await supabase.storage.from(bucket).upload(path, file, {
    cacheControl: '3600',
    upsert: false,
    contentType: file.type,
  });
  if (error) throw error;
  return supabase.storage.from(bucket).getPublicUrl(path).data.publicUrl;
}

/** Sube un archivo a un bucket privado y devuelve la ruta interna del objeto. */
export async function uploadPrivateFile(
  bucket: string,
  file: File,
  folder = 'items',
): Promise<string> {
  const path = buildPath(folder, file);
  const { error } = await supabase.storage.from(bucket).upload(path, file, {
    cacheControl: '3600',
    upsert: false,
    contentType: file.type,
  });
  if (error) throw error;
  return path;
}

/** Convierte una ruta interna en URL pública (o devuelve la URL si ya lo es). */
export function resolvePublicUrl(bucket: string, pathOrUrl: string | null | undefined): string {
  if (!pathOrUrl) return '';
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  return supabase.storage.from(bucket).getPublicUrl(pathOrUrl).data.publicUrl;
}

/** Genera una URL firmada temporal para acceder a un archivo privado. */
export async function getSignedUrl(
  bucket: string,
  path: string | null | undefined,
  expiresIn = 3600,
): Promise<string | null> {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  const { data, error } = await supabase.storage.from(bucket).createSignedUrl(path, expiresIn);
  if (error || !data) return null;
  return data.signedUrl;
}

export function removeStorageFile(bucket: string, path: string): Promise<void> {
  return supabase.storage
    .from(bucket)
    .remove([path])
    .then(() => undefined)
    .catch(() => undefined);
}